"use client";

import { useMemo } from "react";
import { Text, VStack } from "@chakra-ui/react";
import { Situation } from "@/generated/fastapi";
import GenericConfirmationDialog from "@/shared/ui/GenericConfirmationDialog";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import {
  filterSituationsByPrototypes,
  useSituationItems,
  useSituationsQuery,
} from "@/features/situations";
import {
  PROFILE_SHAPING_LABELS,
  PROFILE_SHAPING_MESSAGES,
} from "@/features/profile-shaping";
import { useSuggestedThoughtConfirmationContext } from "../contexts/SuggestedThoughtConfirmationProvider";

export default function SuggestedThoughtConfirmationDialog() {
  const situationsQuery = useSituationsQuery();
  const {
    reassignSingleThoughtToSituation,
    setReassignSingleThoughtToSituation,
    confirmationPrototypes,
    setConfirmationPrototypes,
    confirmSingleThoughtConfirmation,
  } = useSuggestedThoughtConfirmationContext();
  const availableSituations = useMemo(
    () =>
      filterSituationsByPrototypes(
        situationsQuery.data ?? [],
        confirmationPrototypes,
      ),
    [confirmationPrototypes, situationsQuery.data],
  );
  const situationItems = useSituationItems(availableSituations);
  const canConfirm = reassignSingleThoughtToSituation.length > 0;
  const hasAvailableSituations = availableSituations.length > 0;

  return (
    <GenericConfirmationDialog
      isOpen={confirmSingleThoughtConfirmation.isOpen}
      onCloseAction={() =>
        confirmSingleThoughtConfirmation.onCloseWithCleanup(() => {
          setReassignSingleThoughtToSituation([]);
          setConfirmationPrototypes([]);
        })
      }
      onConfirmAction={async () => {
        await confirmSingleThoughtConfirmation.onConfirm({
          situationId: reassignSingleThoughtToSituation[0]?.id,
        });
        setReassignSingleThoughtToSituation([]);
        setConfirmationPrototypes([]);
      }}
      title={PROFILE_SHAPING_LABELS.anchorAndConfirm}
      description={PROFILE_SHAPING_MESSAGES.anchorAndConfirmThought}
      confirmText={PROFILE_SHAPING_LABELS.anchorAndConfirm}
      confirmDisabled={!canConfirm}
    >
      <VStack align="stretch">
        {hasAvailableSituations ? (
          <>
            <Text>Choose where this thought should be anchored:</Text>
            <GenericSelector<Situation>
              previewItems={situationItems}
              stateObject={reassignSingleThoughtToSituation}
              stateObjectSetter={setReassignSingleThoughtToSituation}
              mode="select"
              sourceItems={availableSituations}
              placeholder="Anchor to situation"
            />
          </>
        ) : (
          <GenericEmptyComponent
            title="No compatible situations"
            message="Add or edit a situation that supports this perspective before confirming."
            icon="situation"
            variant="inline"
          />
        )}
      </VStack>
    </GenericConfirmationDialog>
  );
}
